import React, { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROPERTY_TYPES = [
  { v: "office", l: "Office Building" },
  { v: "retail", l: "Retail / Strip Center" },
  { v: "industrial", l: "Industrial / Manufacturing" },
  { v: "industrial_building", l: "Industrial Building" },
  { v: "multifamily", l: "Multifamily / Apartments" },
  { v: "hotel", l: "Hotel / Hospitality" },
  { v: "medical", l: "Medical Office" },
  { v: "dental_office", l: "Dental Office" },
  { v: "daycare", l: "Daycare" },
  { v: "assisted_living", l: "Assisted Living" },
  { v: "warehouse", l: "Warehouse / Distribution" },
  { v: "mixed_use", l: "Mixed-Use" },
  { v: "short_term_rental", l: "Short-Term Rental" },
  { v: "long_term_rental", l: "Long-Term Rental" },
  { v: "townhouse", l: "Townhouse" },
  { v: "condo", l: "Condo" },
  { v: "duplex", l: "Duplex" },
];

const fmt = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n || 0);

export default function Estimator({ onResult }) {
  const [form, setForm] = useState({
    property_type: "multifamily",
    purchase_price: 0,
    land_percentage: 20,
    year_placed_in_service: new Date().getFullYear(),
    federal_tax_rate: 37,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const years = useMemo(() => {
    const y = [];
    const current = new Date().getFullYear();
    for (let i = current + 1; i >= 2000; i--) y.push(i);
    return y;
  }, []);

  const submit = async (e) => {
    e?.preventDefault();
    if (!Number(form.purchase_price) || Number(form.purchase_price) <= 0) {
      toast.error("Enter a purchase price to calculate your savings.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/estimate`, {
        ...form,
        purchase_price: Number(form.purchase_price),
        land_percentage: Number(form.land_percentage),
        year_placed_in_service: Number(form.year_placed_in_service),
        federal_tax_rate: Number(form.federal_tax_rate),
        state_tax_rate: 0,
      });
      setResult(data);
      onResult?.({ ...data, ...form });
    } catch (err) {
      toast.error("Could not compute estimate. Please check your inputs.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="estimator"
      data-testid="estimator-section"
      className="py-24 sm:py-32 bg-ivory-200 relative"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-end mb-14">
          <div className="lg:col-span-7">
            <p className="text-forest-600 uppercase tracking-[0.24em] text-sm mb-4">
              Tax Savings Calculator
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-forest-950 leading-[1.05] tracking-tight">
              See what your property <br />
              <span className="italic text-brass">could put back in your pocket.</span>
            </h2>
          </div>
          <p className="lg:col-span-5 text-forest-800 text-lg leading-relaxed">
            Engineering rule-of-thumb ratios applied to current federal bonus
            depreciation rules. A free, no-obligation snapshot — the formal
            study refines the number significantly.
          </p>
        </div>

        <form
          onSubmit={submit}
          className="grid lg:grid-cols-12 gap-px bg-ivory-400 border border-ivory-400"
        >
          {/* INPUTS */}
          <div className="lg:col-span-7 bg-white p-8 sm:p-12">
            <div className="grid sm:grid-cols-2 gap-6">
              <Field label="Property Type">
                <select
                  value={form.property_type}
                  onChange={(e) => update("property_type", e.target.value)}
                  data-testid="estimator-property-type"
                  className="w-full bg-white border border-ivory-400 px-4 py-3 text-forest-900 focus:outline-none focus:border-brass"
                >
                  {PROPERTY_TYPES.map((p) => (
                    <option key={p.v} value={p.v}>{p.l}</option>
                  ))}
                </select>
              </Field>

              <Field label="Year Placed in Service">
                <select
                  value={form.year_placed_in_service}
                  onChange={(e) => update("year_placed_in_service", e.target.value)}
                  data-testid="estimator-year"
                  className="w-full bg-white border border-ivory-400 px-4 py-3 text-forest-900 focus:outline-none focus:border-brass"
                >
                  {years.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </Field>

              <Field label="Purchase Price (USD)">
                <input
                  type="number"
                  min="50000"
                  step="50000"
                  value={form.purchase_price}
                  onChange={(e) => update("purchase_price", e.target.value)}
                  data-testid="estimator-purchase-price"
                  className="w-full bg-white border border-ivory-400 px-4 py-3 text-forest-900 focus:outline-none focus:border-brass tabular"
                />
              </Field>

              <Field label={`Land Allocation: ${form.land_percentage}%`}>
                <input
                  type="range"
                  min="0"
                  max="60"
                  value={form.land_percentage}
                  onChange={(e) => update("land_percentage", e.target.value)}
                  data-testid="estimator-land-percentage"
                  className="w-full accent-brass"
                />
              </Field>

              <Field label={`Federal Tax Rate: ${form.federal_tax_rate}%`}>
                <input
                  type="range"
                  min="10"
                  max="40"
                  value={form.federal_tax_rate}
                  onChange={(e) => update("federal_tax_rate", e.target.value)}
                  data-testid="estimator-fed-rate"
                  className="w-full accent-brass"
                />
              </Field>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-forest-900 hover:bg-forest-800 text-ivory px-8 py-4 font-medium transition-colors disabled:opacity-60"
              data-testid="estimator-submit"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : null}
              Calculate My Savings
            </button>
          </div>

          {/* OUTPUT */}
          <div className="lg:col-span-5 bg-forest-950 text-ivory p-8 sm:p-12 relative">
            <p className="text-brass uppercase tracking-[0.24em] text-xs mb-6">
              Estimated Year-One Impact
            </p>

            <ResultRow
              label="Depreciable Basis"
              value={fmt(result?.depreciable_basis)}
              testid="result-depreciable"
            />
            <ResultRow
              label="Reclassified to 5/7-yr"
              value={fmt(result?.reclassified_5_7_year)}
              testid="result-5-7"
            />
            <ResultRow
              label="Reclassified to 15-yr"
              value={fmt(result?.reclassified_15_year)}
              testid="result-15"
            />
            <ResultRow
              label={`Bonus Depreciation (${result ? Math.round(result.bonus_depreciation_rate * 100) : "—"}%)`}
              value={fmt(result?.accelerated_first_year_deduction)}
              testid="result-bonus"
              highlight
            />

            <div className="mt-8 pt-8 border-t border-ivory/20">
              <p className="text-ivory/70 uppercase tracking-[0.22em] text-xs mb-3">
                Estimated Cash Savings
              </p>
              <div
                className="font-sans text-5xl sm:text-6xl text-brass tabular tracking-tight"
                data-testid="result-cash-savings"
              >
                {fmt(result?.estimated_cash_savings)}
              </div>
              <p className="text-ivory/60 text-sm mt-4 leading-relaxed">
                Estimate based on engineering-grade reclassification ratios and
                current bonus depreciation schedule. A formal study typically
                refines this figure by ±10–15%.
              </p>
              {result && (
                <a
                  href="#contact"
                  className="mt-6 inline-flex items-center gap-2 bg-brass hover:bg-brass-500 text-forest-900 px-6 py-3 text-sm font-medium transition-colors"
                  data-testid="estimator-cta-claim"
                >
                  Claim these savings →
                </a>
              )}
            </div>
          </div>
        </form>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-forest-600 uppercase tracking-[0.2em] text-xs mb-3">
        {label}
      </span>
      {children}
    </label>
  );
}

function ResultRow({ label, value, testid, highlight = false }) {
  return (
    <div
      className={`flex items-baseline justify-between py-3 border-b border-ivory/10 ${
        highlight ? "text-ivory" : "text-ivory/80"
      }`}
    >
      <span className="text-sm">{label}</span>
      <span
        className={`font-sans tabular ${highlight ? "text-ivory text-xl" : "text-base"}`}
        data-testid={testid}
      >
        {value}
      </span>
    </div>
  );
}
